import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Upload, X, FileText, Image, File } from "lucide-react";
import { Button } from "../../AdminSharedComponents";

const FileField = ({
  label,
  name,
  value = [],
  onChange,
  onRemove,
  currentAttachments = [],
  pendingRemovals = [],
  onMarkForRemoval,
  onUndoRemoval,
  multiple = true,
  accept,
  maxSize = 10, // MB
  disabled = false,
  required = false,
  className = "",
  variant = "default", // "default", "compact", "grid", "minimal", "single"
  onFileSizeError, // Callback for file size errors
  ...props
}) => {
  // Track blob URLs to prevent memory leaks
  const blobUrlsRef = useRef(new Map()); // Use Map to track file->url mapping
  const [imageErrors, setImageErrors] = useState(new Set());

  // Memoize file validation
  const validateFiles = useCallback(
    (files) => {
      const invalidFiles = files.filter(
        (file) => file.size > maxSize * 1024 * 1024
      );
      if (invalidFiles.length > 0) {
        // Use callback to show modal instead of alert
        if (onFileSizeError) {
          onFileSizeError(invalidFiles);
        } else {
          // Fallback to alert if no callback provided
          alert(`Some files exceed the ${maxSize}MB limit and were not added.`);
        }
        return false;
      }
      return true;
    },
    [maxSize, onFileSizeError]
  );

  // Handle file input change with useCallback to prevent re-renders
  const handleFileChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      if (!validateFiles(files)) {
        e.target.value = ""; // Reset input
        return;
      }

      // For single variant, only take the first file
      if (variant === "single") {
        onChange([files[0]]);
      } else {
        onChange(files);
      }

      // Reset input value to allow same file selection
      e.target.value = "";
    },
    [onChange, variant, validateFiles]
  );

  // Memoize file icon to prevent unnecessary re-renders
  const getFileIcon = useCallback((file) => {
    if (!file) return <File className="h-5 w-5" />;

    const fileType = file.type || "";
    const fileName = file.name || "";

    if (fileType.startsWith("image/")) {
      return <Image className="h-5 w-5 text-green-600" />;
    } else if (fileType === "application/pdf" || fileName.endsWith(".pdf")) {
      return <FileText className="h-5 w-5 text-red-600" />;
    } else if (
      fileType.includes("word") ||
      fileName.endsWith(".doc") ||
      fileName.endsWith(".docx")
    ) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    } else if (
      fileType.includes("excel") ||
      fileName.endsWith(".xls") ||
      fileName.endsWith(".xlsx")
    ) {
      return <FileText className="h-5 w-5 text-green-600" />;
    } else if (
      fileType.includes("zip") ||
      fileType.includes("rar") ||
      fileType.includes("7z") ||
      fileType.includes("tar") ||
      fileType.includes("gzip") ||
      fileName.endsWith(".zip") ||
      fileName.endsWith(".rar") ||
      fileName.endsWith(".7z") ||
      fileName.endsWith(".tar") ||
      fileName.endsWith(".gz")
    ) {
      return <FileText className="h-5 w-5 text-purple-600" />;
    } else {
      return <File className="h-5 w-5 text-gray-600" />;
    }
  }, []);

  // Memoize file size formatter
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrls.clear();
    };
  }, []);

  // Cleanup blob URLs when files change
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    const currentFiles = new Set(
      value.map((file) => file.name + file.size + file.lastModified)
    );

    // Revoke any blob URLs that are no longer needed
    blobUrls.forEach((url, fileKey) => {
      if (!currentFiles.has(fileKey)) {
        URL.revokeObjectURL(url);
        blobUrls.delete(fileKey);
      }
    });
  }, [value]);

  // Memoize preview creation with proper cleanup
  const createPreview = useCallback((file) => {
    if (file.type?.startsWith("image/")) {
      const fileKey = file.name + file.size + file.lastModified;
      const blobUrls = blobUrlsRef.current;

      // Check if we already have a blob URL for this file
      if (blobUrls.has(fileKey)) {
        return blobUrls.get(fileKey);
      }

      // Create new blob URL
      const blobUrl = URL.createObjectURL(file);
      blobUrls.set(fileKey, blobUrl);
      return blobUrl;
    }
    return null;
  }, []);

  // Cleanup specific blob URL by file (for future use)
  // const revokeBlobUrlByFile = useCallback((file) => {
  //   if (file && file.type?.startsWith("image/")) {
  //     const fileKey = file.name + file.size + file.lastModified;
  //     const blobUrls = blobUrlsRef.current;

  //     if (blobUrls.has(fileKey)) {
  //       const url = blobUrls.get(fileKey);
  //       URL.revokeObjectURL(url);
  //       blobUrls.delete(fileKey);
  //     }
  //   }
  // }, []);

  // Handle image load error
  const handleImageError = useCallback((fileKey) => {
    setImageErrors((prev) => new Set([...prev, fileKey]));
  }, []);

  // Memoize active attachments calculation
  const activeAttachments = useMemo(() => {
    const attachments = [];

    // Add existing attachments (exclude pending removals)
    if (currentAttachments && currentAttachments.length > 0) {
      currentAttachments
        .filter((attachment) => !pendingRemovals.includes(attachment.id))
        .forEach((attachment) => {
          attachments.push({
            type: "existing",
            id: attachment.id,
            name:
              attachment.original_name ||
              attachment.file_name ||
              attachment.originalname ||
              attachment.filename ||
              "File",
            size: attachment.size || 0,
            file: {
              type: attachment.file_type || attachment.mimetype || "",
              name:
                attachment.file_name ||
                attachment.filename ||
                attachment.name ||
                "",
            },
            url: attachment.file_path || attachment.url,
          });
        });
    }

    // Add new selected files
    if (value && value.length > 0) {
      value.forEach((file, index) => {
        attachments.push({
          type: "new",
          index: index,
          name: file.name,
          size: file.size,
          file: file,
          preview: createPreview(file),
        });
      });
    }

    return attachments;
  }, [currentAttachments, pendingRemovals, value, createPreview]);

  // Memoize pending removal attachments
  const pendingRemovalAttachments = useMemo(() => {
    if (!currentAttachments || pendingRemovals.length === 0) {
      return [];
    }

    return currentAttachments
      .filter((attachment) => pendingRemovals.includes(attachment.id))
      .map((attachment) => ({
        type: "pending-removal",
        id: attachment.id,
        name:
          attachment.original_name ||
          attachment.file_name ||
          attachment.originalname ||
          attachment.filename ||
          "File",
        size: attachment.size || 0,
        file: {
          type: attachment.file_type || attachment.mimetype || "",
          name:
            attachment.file_name ||
            attachment.filename ||
            attachment.name ||
            "",
        },
      }));
  }, [currentAttachments, pendingRemovals]);

  // Upload Area Component with useMemo to prevent re-renders
  const UploadArea = useMemo(() => {
    const UploadAreaComponent = ({ className: uploadClassName = "" }) => (
      <div
        className={`border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors ${uploadClassName}`}
      >
        <label
          htmlFor={name}
          className={`flex flex-col items-center justify-center p-6 cursor-pointer ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 mb-4 flex items-center justify-center bg-gray-100 rounded-full">
              <Upload className="h-6 w-6 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700 mb-1">
                {activeAttachments.length > 0
                  ? "Add More Files"
                  : "Upload Files"}
              </p>
              <p className="text-xs text-gray-500">
                Click to browse or drag and drop
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PNG, JPG, GIF, PDF, DOC, DOCX, XLS, XLSX up to {maxSize}MB each
              </p>
            </div>
          </div>
        </label>
        <input
          id={name}
          name={name}
          type="file"
          className="sr-only"
          multiple={multiple}
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled}
          {...props}
        />
      </div>
    );
    return UploadAreaComponent;
  }, [
    name,
    disabled,
    activeAttachments.length,
    maxSize,
    multiple,
    accept,
    handleFileChange,
    props,
  ]);

  // File Item Component with useCallback to prevent re-renders
  const FileItem = useCallback(
    ({ attachment, onRemoveClick, showPreview = true }) => {
      const fileKey = attachment.file
        ? attachment.file.name +
          attachment.file.size +
          attachment.file.lastModified
        : null;

      const hasError = fileKey ? imageErrors.has(fileKey) : false;

      return (
        <div
          className={`relative group border rounded-lg p-3 transition-all duration-200 ${
            attachment.type === "existing"
              ? "bg-blue-50 border-blue-200"
              : attachment.type === "new"
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200 opacity-60"
          }`}
        >
          {/* File Content */}
          <div className="flex items-start gap-3">
            {/* Preview or Icon */}
            <div className="flex-shrink-0">
              {showPreview && attachment.preview && !hasError ? (
                <div className="w-12 h-12 overflow-hidden rounded border border-gray-200">
                  <img
                    src={attachment.preview}
                    alt="Preview"
                    className="h-full w-full object-cover"
                    onError={() => fileKey && handleImageError(fileKey)}
                  />
                </div>
              ) : showPreview &&
                attachment.url &&
                attachment.file.type?.startsWith("image/") &&
                !hasError ? (
                <div className="w-12 h-12 overflow-hidden rounded border border-gray-200">
                  <img
                    src={attachment.url}
                    alt="Preview"
                    className="h-full w-full object-cover"
                    onError={() => fileKey && handleImageError(fileKey)}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center w-10 h-10 bg-white rounded border border-gray-200">
                  {hasError ? (
                    <div className="text-red-500 text-xs text-center">
                      Error
                    </div>
                  ) : (
                    getFileIcon(attachment.file)
                  )}
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {attachment.name}
              </p>
              {attachment.size > 0 && (
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}
                </p>
              )}
              {/* Status Badge */}
              <div className="mt-1">
                {attachment.type === "existing" && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Existing
                  </span>
                )}
                {attachment.type === "new" && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    New
                  </span>
                )}
                {attachment.type === "pending-removal" && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Will be deleted
                  </span>
                )}
              </div>
            </div>

            {/* Remove Button */}
            <Button
              variant={
                attachment.type === "pending-removal" ? "secondary" : "danger"
              }
              onClick={onRemoveClick}
              disabled={disabled}
              className="!p-2 !min-w-0"
            >
              {attachment.type === "pending-removal" ? (
                <span className="text-xs px-1">Undo</span>
              ) : (
                <X className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      );
    },
    [getFileIcon, formatFileSize, disabled, imageErrors, handleImageError]
  );

  // Create remove handlers with useCallback
  const handleRemoveExisting = useCallback(
    (id) => {
      onMarkForRemoval && onMarkForRemoval(id);
    },
    [onMarkForRemoval]
  );

  const handleRemoveNew = useCallback(
    (index) => {
      onRemove && onRemove(index);
    },
    [onRemove]
  );

  const handleUndoRemoval = useCallback(
    (id) => {
      onUndoRemoval && onUndoRemoval(id);
    },
    [onUndoRemoval]
  );

  // Render based on variant
  const renderContent = useCallback(() => {
    const UploadAreaComponent = UploadArea;

    switch (variant) {
      case "single":
        return (
          <div className="space-y-4">
            {/* Upload area jika belum ada file */}
            {activeAttachments.length === 0 && (
              <UploadAreaComponent className="p-8 bg-gray-50 hover:bg-gray-100" />
            )}

            {/* Jika ada file terpilih */}
            {activeAttachments.length > 0 && (
              <div className="relative border rounded-lg overflow-hidden shadow-sm group">
                {/* Preview untuk image */}
                {activeAttachments[0].preview ||
                (activeAttachments[0].url &&
                  activeAttachments[0].file.type?.startsWith("image/")) ? (
                  <img
                    src={
                      activeAttachments[0].preview || activeAttachments[0].url
                    }
                    alt="Preview"
                    className="w-full h-56 object-cover"
                    onError={() => {
                      const file = activeAttachments[0].file;
                      if (file) {
                        const fileKey =
                          file.name + file.size + file.lastModified;
                        handleImageError(fileKey);
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-40 bg-gray-50">
                    {getFileIcon(activeAttachments[0].file)}
                  </div>
                )}

                {/* Info bar */}
                <div className="p-3 flex items-center justify-between bg-white border-t">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activeAttachments[0].name}
                    </p>
                    {activeAttachments[0].size > 0 && (
                      <p className="text-xs text-gray-500">
                        {formatFileSize(activeAttachments[0].size)}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {/* Replace */}
                    <label htmlFor={`${name}-replace`}>
                      <Button
                        variant="secondary"
                        disabled={disabled}
                        className="cursor-pointer"
                        as="span"
                      >
                        Replace
                      </Button>
                    </label>
                    <input
                      id={`${name}-replace`}
                      name={name}
                      type="file"
                      className="sr-only"
                      multiple={false}
                      accept={accept}
                      onChange={handleFileChange}
                      disabled={disabled}
                      {...props}
                    />

                    {/* Remove */}
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (activeAttachments[0].type === "existing") {
                          handleRemoveExisting(activeAttachments[0].id);
                        } else {
                          handleRemoveNew(activeAttachments[0].index);
                        }
                      }}
                      disabled={disabled}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Pending removal notice */}
            {pendingRemovalAttachments.length > 0 && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-primary-800">
                    File will be deleted after saving
                  </h4>
                  <p className="text-xs text-primary-600">
                    {pendingRemovalAttachments[0].name}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() =>
                    handleUndoRemoval(pendingRemovalAttachments[0].id)
                  }
                  disabled={disabled}
                  className="!text-xs !px-3 !py-1"
                >
                  Undo
                </Button>
              </div>
            )}
          </div>
        );

      case "compact":
        return (
          <div className="space-y-4">
            {/* Upload area always at top */}
            <UploadAreaComponent />

            {/* Files in compact list */}
            {activeAttachments.length > 0 && (
              <div className="space-y-2">
                {activeAttachments.map((attachment, index) => (
                  <FileItem
                    key={`${attachment.type}-${
                      attachment.id || attachment.index
                    }`}
                    attachment={attachment}
                    showPreview={false}
                    onRemoveClick={() => {
                      if (attachment.type === "existing") {
                        handleRemoveExisting(attachment.id);
                      } else {
                        handleRemoveNew(attachment.index);
                      }
                    }}
                  />
                ))}
              </div>
            )}

            {/* Pending removals */}
            {pendingRemovalAttachments.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <h4 className="text-sm font-medium text-amber-800 mb-2">
                  Files marked for removal ({pendingRemovalAttachments.length})
                </h4>
                <div className="space-y-2">
                  {pendingRemovalAttachments.map((attachment) => (
                    <FileItem
                      key={`pending-${attachment.id}`}
                      attachment={attachment}
                      showPreview={false}
                      onRemoveClick={() => handleUndoRemoval(attachment.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "grid":
        return (
          <div className="space-y-4">
            {/* Upload area always at top */}
            <UploadAreaComponent />

            {/* Files in grid */}
            {activeAttachments.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {activeAttachments.map((attachment, index) => (
                  <div
                    key={`${attachment.type}-${
                      attachment.id || attachment.index
                    }`}
                    className={`relative group border rounded-lg p-4 transition-all duration-200 ${
                      attachment.type === "existing"
                        ? "bg-blue-50 border-blue-200"
                        : "bg-green-50 border-green-200"
                    }`}
                  >
                    {/* Preview or Icon */}
                    <div className="flex flex-col items-center">
                      {attachment.preview ? (
                        <div className="w-16 h-16 mb-3 overflow-hidden rounded-lg border border-gray-200">
                          <img
                            src={attachment.preview}
                            alt="Preview"
                            className="h-full w-full object-cover"
                            onError={() => {
                              const fileKey =
                                attachment.file.name +
                                attachment.file.size +
                                attachment.file.lastModified;
                              handleImageError(fileKey);
                            }}
                          />
                        </div>
                      ) : attachment.url &&
                        attachment.file.type?.startsWith("image/") ? (
                        <div className="w-16 h-16 mb-3 overflow-hidden rounded-lg border border-gray-200">
                          <img
                            src={attachment.url}
                            alt="Preview"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-12 h-12 mb-3 bg-white rounded-lg shadow-sm border border-gray-200">
                          {getFileIcon(attachment.file)}
                        </div>
                      )}

                      {/* File Info */}
                      <p className="text-sm font-medium text-gray-700 text-center truncate max-w-full px-1">
                        {attachment.name}
                      </p>

                      {attachment.size > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {formatFileSize(attachment.size)}
                        </p>
                      )}

                      {/* Status Badge */}
                      <div className="mt-2">
                        {attachment.type === "existing" && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Existing
                          </span>
                        )}
                        {attachment.type === "new" && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            New
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Button
                        variant="danger"
                        onClick={() => {
                          if (attachment.type === "existing") {
                            handleRemoveExisting(attachment.id);
                          } else {
                            handleRemoveNew(attachment.index);
                          }
                        }}
                        disabled={disabled}
                        className="!p-1 !min-w-0 rounded-full"
                        icon={X}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pending removals */}
            {pendingRemovalAttachments.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-amber-800 mb-3">
                  Files marked for removal ({pendingRemovalAttachments.length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingRemovalAttachments.map((attachment) => (
                    <div
                      key={`pending-${attachment.id}`}
                      className="flex flex-col items-center border border-amber-200 rounded-lg p-3 bg-white/70"
                    >
                      <div className="flex items-center justify-center w-12 h-12 mb-2 bg-gray-100 rounded-lg">
                        {getFileIcon(attachment.file)}
                      </div>

                      <p className="text-sm text-gray-600 font-medium text-center truncate max-w-full px-1 line-through">
                        {attachment.name}
                      </p>

                      <Button
                        variant="secondary"
                        onClick={() => handleUndoRemoval(attachment.id)}
                        disabled={disabled}
                        className="!text-xs !px-3 !py-1 mt-2"
                      >
                        Undo
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "minimal":
        return (
          <div className="space-y-3">
            {/* Simple upload area */}
            <div className="border border-gray-300 rounded-lg">
              <label
                htmlFor={name}
                className={`flex items-center justify-center p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Upload className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-700">
                  {activeAttachments.length > 0
                    ? `Add more files (${activeAttachments.length} selected)`
                    : "Choose files"}
                </span>
              </label>
              <input
                id={name}
                name={name}
                type="file"
                className="sr-only"
                multiple={multiple}
                accept={accept}
                onChange={handleFileChange}
                disabled={disabled}
                {...props}
              />
            </div>

            {/* Simple file list */}
            {activeAttachments.length > 0 && (
              <div className="text-xs text-gray-600 space-y-1">
                {activeAttachments.map((attachment, index) => (
                  <div
                    key={`${attachment.type}-${
                      attachment.id || attachment.index
                    }`}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="truncate mr-2">{attachment.name}</span>
                    <Button
                      variant="text"
                      onClick={() => {
                        if (attachment.type === "existing") {
                          handleRemoveExisting(attachment.id);
                        } else {
                          handleRemoveNew(attachment.index);
                        }
                      }}
                      disabled={disabled}
                      className="!p-1 !min-w-0 text-red-600 hover:text-red-800"
                      icon={X}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default: // "default" variant
        return (
          <div className="space-y-4">
            {/* Upload area always visible at top */}
            <UploadAreaComponent />

            {/* Active Files List */}
            {activeAttachments.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <File className="h-4 w-4" />
                  Attached Files ({activeAttachments.length})
                </h4>
                <div className="space-y-2">
                  {activeAttachments.map((attachment, index) => (
                    <FileItem
                      key={`${attachment.type}-${
                        attachment.id || attachment.index
                      }`}
                      attachment={attachment}
                      onRemoveClick={() => {
                        if (attachment.type === "existing") {
                          handleRemoveExisting(attachment.id);
                        } else {
                          handleRemoveNew(attachment.index);
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pending Removal Section */}
            {pendingRemovalAttachments.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Files marked for removal ({pendingRemovalAttachments.length})
                  <span className="text-xs font-normal text-amber-600">
                    (will be deleted after saving)
                  </span>
                </h4>
                <div className="space-y-2">
                  {pendingRemovalAttachments.map((attachment) => (
                    <FileItem
                      key={`pending-${attachment.id}`}
                      attachment={attachment}
                      showPreview={false}
                      onRemoveClick={() => handleUndoRemoval(attachment.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
    }
  }, [
    variant,
    activeAttachments,
    pendingRemovalAttachments,
    UploadArea,
    handleFileChange,
    handleRemoveExisting,
    handleRemoveNew,
    handleUndoRemoval,
    getFileIcon,
    formatFileSize,
    handleImageError,
    name,
    disabled,
    accept,
    multiple,
    props,
  ]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Content based on variant */}
      {renderContent()}
    </div>
  );
};

export default FileField;
